<template>
  <v-card class="mt-4 ml-2 mr-2" elevation="6">
    <v-skeleton-loader v-if="loadingPlans" type="heading, paragraph, actions"></v-skeleton-loader>

    <v-alert v-else-if="!offer" type="error" variant="tonal" class="ma-4">
      Impossible de charger les offres pour le moment.
      <template #append>
        <v-btn variant="text" @click="fetchSubscriptionPlans">Réessayer</v-btn>
      </template>
    </v-alert>

    <template v-else>
      <v-card-item>
        <template #prepend>
          <v-avatar color="primary" variant="tonal" size="48">
            <v-icon>mdi-crown-outline</v-icon>
          </v-avatar>
        </template>
        <v-card-title>{{ offer.plan.name }}</v-card-title>
        <v-card-subtitle class="text-wrap">Devenez membre et soutenez la plateforme</v-card-subtitle>
        <template #append>
          <div class="text-right">
            <div class="text-headline-small font-weight-bold text-primary">{{ offer.amount }}</div>
            <div v-if="offer.period" class="text-body-small text-medium-emphasis">{{ offer.period }}</div>
          </div>
        </template>
      </v-card-item>

      <v-divider></v-divider>

      <v-list lines="three" density="comfortable">
        <v-list-item v-for="feature in features" :key="feature.title" :prepend-icon="feature.icon">
          <v-list-item-title class="font-weight-medium text-wrap">{{ feature.title }}</v-list-item-title>
          <v-list-item-subtitle>{{ feature.text }}</v-list-item-subtitle>
        </v-list-item>
      </v-list>

      <v-card-text>
        <v-alert v-if="offer.isRecurring" type="info" variant="tonal">
          <div class="font-weight-bold mb-1">Renouvellement automatique</div>
          L'abonnement se renouvelle chaque année. Pour éviter le renouvellement, ouvrez votre portail d'abonnement
          (menu du compte › Mon abonnement) et cliquez sur « Annuler l'abonnement ». Votre abonnement restera actif
          jusqu'à la fin de la période payée.
        </v-alert>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-btn color="primary" variant="flat" :disabled="!canSubscribe" :loading="isLoading" @click="subscribe">
          Je m'abonne
        </v-btn>
        <v-btn v-if="!isAuthenticated" color="primary" variant="outlined" :to="{ name: 'nouvel-utilisateur' }">
          Créer un compte
        </v-btn>
      </v-card-actions>
      <v-card-text v-if="!isAuthenticated" class="pt-0 text-body-small">
        Connectez-vous ou créez un compte pour vous abonner.
      </v-card-text>
    </template>
  </v-card>
</template>

<script>
  import { auth, functions } from "@/firebase";
  import { notifyError } from "@/services/Notification.service";

  const PERIOD_LABELS = { year: "par année", month: "par mois" };

  // Membership offer with its Stripe checkout. Shown by StripePortal.vue to visitors who are not members.
  export default {
    name: "Subscription",
    data() {
      return {
        plans: [],
        loadingPlans: true,
        isLoading: false,
        currentUser: auth.currentUser,
        unsubscribeAuth: null,
        features: [
          {
            icon: "mdi-waves",
            title: "Accès complet aux débits en temps réel",
            text: "Des détails précis sur les débits des rivières pour planifier vos sorties en toute confiance.",
          },
          {
            icon: "mdi-chart-bell-curve-cumulative",
            title: "Extrapolation avancée des données",
            text: "Des données extrapolées pour les rivières sans station hydrométrique.",
          },
          {
            icon: "mdi-webcam",
            title: "Conditions en direct",
            text: "Un aperçu en temps réel des rapides grâce aux webcams.",
          },
          {
            icon: "mdi-map-legend",
            title: "Cartes topographiques et points clés",
            text: "Mises à l'eau, sorties et rapides importants sur des cartes détaillées.",
          },
          {
            icon: "mdi-hand-heart-outline",
            title: "Soutenez la communauté",
            text: "Votre adhésion finance la plateforme, le matériel et les mises à jour régulières.",
          },
        ],
      };
    },
    computed: {
      isAuthenticated() {
        return Boolean(this.currentUser);
      },
      // The yearly price of the first plan that has one, otherwise the first price: never depends on the order
      // in which Stripe lists the prices
      offer() {
        const plans = this.plans.filter((plan) => plan.prices.length > 0);
        const isYearly = (price) => price.type === "recurring" && price.interval === "year";
        const plan = plans.find((candidate) => candidate.prices.some(isYearly)) || plans[0];
        if (!plan) {
          return null;
        }
        const price = plan.prices.find(isYearly) || plan.prices[0];
        const amount = new Intl.NumberFormat("fr-CA", {
          style: "currency",
          currency: price.currency.toUpperCase(),
          minimumFractionDigits: price.unit_amount % 100 === 0 ? 0 : 2,
        }).format(price.unit_amount / 100);
        const isRecurring = price.type === "recurring";
        return {
          plan,
          price,
          amount,
          isRecurring,
          period: isRecurring ? PERIOD_LABELS[price.interval] || "" : "paiement unique",
        };
      },
      canSubscribe() {
        return this.isAuthenticated && Boolean(this.offer) && !this.isLoading;
      },
    },
    created() {
      this.unsubscribeAuth = auth.onAuthStateChanged((user) => {
        this.currentUser = user;
      });
      this.fetchSubscriptionPlans();
    },
    beforeUnmount() {
      if (this.unsubscribeAuth) {
        this.unsubscribeAuth();
      }
    },
    methods: {
      async fetchSubscriptionPlans() {
        this.loadingPlans = true;
        try {
          const listPlans = functions.httpsCallable("stripeListPlans");
          const { data } = await listPlans();
          this.plans = data.plans || [];
        } catch (error) {
          console.error("Unable to load the plans:", error);
          this.plans = [];
        } finally {
          this.loadingPlans = false;
        }
      },
      async subscribe() {
        if (!this.canSubscribe) {
          return;
        }

        this.isLoading = true;
        try {
          const createCheckoutSession = functions.httpsCallable("stripeCreateCheckoutSession");
          const { data } = await createCheckoutSession({
            priceId: this.offer.price.id,
            returnUrl: window.location.origin,
          });
          window.location.assign(data.url);
        } catch (error) {
          // Details are in the Cloud Function logs in the Firebase console.
          console.error("Unable to start the checkout:", error);
          notifyError("Impossible d'ouvrir la page de paiement. Veuillez réessayer.");
          this.isLoading = false;
        }
      },
    },
  };
</script>
